import { MAX_UNSEAMED_SECTION_LENGTH } from '../../../breakpoints/constants';
import { placeRequiredBreakpoints } from '../../../breakpoints/helpers';

import type { ActionRuleParams } from '../../types';
import type { Island } from '../../../islands/island.class';

const applyCountertopSeamForIsland = (island: Island): void => {
	const countertop = island.getCountertop();
	if (!countertop) return;

	placeRequiredBreakpoints({ countertop, maxSectionLength: MAX_UNSEAMED_SECTION_LENGTH });
};

/**
 * Places the countertop seams the material rules require, and clears the ones the design has
 * outgrown.
 *
 * Concrete needs a seam only where a countertop section exceeds 88" (`guide-2026:208-212`);
 * granite needs one at grills, C1SLV, farm sinks and refreshment centres at any size
 * (`guide-2026:213-216`). One engine serves both, parameterised by the triggering cap.
 *
 * Seams this rule places are marked `ruleManaged`, so they are distinguishable from a designer's
 * own breakpoints: only rule-managed seams are ever moved or removed by a re-run, and a re-run
 * never duplicates a seam that already satisfies its demand. See `docs/breakpoint-rules.md`.
 */
export const requiresCountertopSeam = (params: ActionRuleParams): void => {
	const { refMap } = params;
	const { island } = refMap;

	// single-island scope -- apply directly
	if (island) {
		applyCountertopSeamForIsland(island);
		return;
	}

	// kitchen scope -- iterate all islands
	const islands = refMap.islands ?? refMap.kitchen?.design?.islands ?? null;
	if (!islands) return;

	for (const child of islands.children) {
		applyCountertopSeamForIsland(child as Island);
	}
};
